import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    Box,
    Button,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import {LoadingButton} from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BlockIcon from "@mui/icons-material/Block";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RedoIcon from "@mui/icons-material/Redo";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {useTranslation} from "react-i18next";
import {useMutation} from "react-query";
import {useSnackbar} from "notistack";
import {isEqual} from "lodash";
import {Audience} from "crqlar-react-common/dist/models/audience/audience.model";
import {
    DataFilterOperator,
    DataType,
    DataTypeOperator,
    Filter,
    FilterGroup,
    FilterRule
} from "crqlar-react-common/dist/models/audience/filter.model";
import {FilterOption} from "crqlar-react-common/dist/models/audience/filterOption.model";
import GenericValueInput from "crqlar-react-common/dist/components/filters/input/GenericValueInput";
import {AudienceAiService} from "crqlar-react-common/dist/services/audience/audienceAi.service";
import {getFilterOptionLabel} from "crqlar-react-common/dist/utils/filter.util";
import {getErrorMessage} from "crqlar-react-common/dist/utils/http.util";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import NewStatusPicker from "crqlar-react-common/dist/components/NewStatusPicker";
import {ElementChip} from "../common/uiElements/base/ElementChip";
import {SingleSelect} from "../common/newSelect";

/**
 * The rules block of the audience dialog, rebuilt (2026-09-03).
 *
 * The model is fixed: rules inside a box all apply (AND), boxes are alternatives (OR, one word
 * between them), and a sub-group inside a box is the one place with an ANY OF / ALL OF choice, and
 * it nests. That is "OR of ANDs" plus NOT per rule, which can express anything the backend can
 * evaluate. Stored filters may still carry a root AND or an OR group from the old builder, so
 * `toCanonical` rewrites them losslessly on the way in (root-AND groups merge into one box, an OR
 * group becomes an ANY OF sub-group) and the fixed shape is what gets saved on the next edit. Every row is one CSS grid line, field › operator
 * › value › ⋮, so columns line up whatever a row's state: "not" is a chip inside the field, a
 * disabled rule is dimmed and struck through, and neither moves a column. A new rule starts
 * empty with the field picker focused; operator and value stay disabled until a field is chosen.
 *
 * The empty state is one outlined sheet: the description leads, "Use a template" (a menu) and "Add a rule
 * by hand" sit beside Generate on a single action row, so the first move is obvious and the block stays short.
 *
 * Stored audiences can be older than the option list: a field the options no longer know shows its raw
 * key, and an operator the field no longer allows stays visible as a disabled "no longer supported"
 * entry, so nothing silently reads as blank.
 *
 * "Change with AI" sends the current filter plus an instruction to the backend and swaps in the full
 * edited filter it returns: one Apply button, and undo is the safety net.
 */

const ROW_HEIGHT = 40
const HISTORY_LIMIT = 50
const ROW_COLUMNS = 'minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 1fr) 34px'
/** Operators that take no value: the operator select stretches over the value column for these. */
const VALUELESS_OPERATORS = new Set<DataTypeOperator>([
    DataTypeOperator.EXISTS,
    DataTypeOperator.NOT_EXISTS,
    DataTypeOperator.TRUE,
    DataTypeOperator.FALSE,
    DataTypeOperator.IN_THIS_YEAR,
    DataTypeOperator.NOT_IN_THIS_YEAR,
])
const NO_VALUES = {doubleValues: [], localDateValues: [], longValues: [], textValues: [], timestampValues: []}

export interface NewAudienceFiltersProps {
    filter: Filter
    setFilter: (filter: Filter) => void
    filterOptions: FilterOption[]
    /** Full option set for label resolution — a template may reference a field the picker hides. */
    labelOptions?: FilterOption[]
    readOnly?: boolean
    /** Starters, rendered only while the filter has no rules. */
    templates?: Audience[]
    templatesLoading?: boolean
    onSelectTemplate?: (template: Audience) => void
    initialQuery?: string
    /** A filter generated from the description; the caller stores the query and may suggest a name. */
    onGenerated?: (filter: Filter, query: string) => void
}

const groupHasRules = (group: FilterGroup): boolean =>
    (group.filterRules?.length ?? 0) > 0 || (group.subGroups ?? []).some(groupHasRules)

const filterHasRules = (filter: Filter): boolean =>
    (filter.filterGroups ?? []).some(groupHasRules)

const groupIsEmpty = (group: FilterGroup): boolean =>
    (group.filterRules?.length ?? 0) === 0 && (group.subGroups?.length ?? 0) === 0

const emptyRule = (): FilterRule => ({field: ''})

const newBox = (): FilterGroup => ({operator: DataFilterOperator.AND, filterRules: [emptyRule()]})

const groupMembers = (group: FilterGroup): number =>
    (group.filterRules?.length ?? 0) + (group.subGroups?.length ?? 0)

/** A stored group as a box: AND groups as they are, an OR group wrapped as one ANY OF sub-group. */
const toBox = (group: FilterGroup): FilterGroup => {
    const isOr = group.operator === DataFilterOperator.OR && groupMembers(group) > 1
    if (!isOr) return {...group, operator: DataFilterOperator.AND}
    return {
        operator: DataFilterOperator.AND,
        filterRules: [],
        subGroups: [{operator: DataFilterOperator.OR, filterRules: group.filterRules, subGroups: group.subGroups}],
    }
}

/**
 * Rewrites any filter into the fixed model without changing what it matches: boxes joined by OR,
 * rules inside a box joined by AND, sub-groups keeping their own operator. A root AND over several
 * groups collapses into one box holding all of them. Returns the same object when nothing changes,
 * so callers comparing against a stored filter keep working.
 */
export const toCanonical = (filter: Filter): Filter => {
    const groups = filter.filterGroups ?? []
    if (groups.length === 0) return filter
    let boxes = groups.map(toBox)
    const rootIsAnd = (filter.operator ?? DataFilterOperator.AND) === DataFilterOperator.AND
    if (rootIsAnd && boxes.length > 1) {
        boxes = [{
            operator: DataFilterOperator.AND,
            filterRules: boxes.flatMap(box => box.filterRules ?? []),
            subGroups: boxes.flatMap(box => box.subGroups ?? []),
        }]
    }
    const canonical: Filter = {...filter, operator: DataFilterOperator.OR, filterGroups: boxes}
    return isEqual(canonical, filter) ? filter : canonical
}

// ------------------------------------------------------------------------------------------- row

interface RuleRowProps {
    rule: FilterRule
    setRule: (rule: FilterRule) => void
    onDelete: () => void
    onDuplicate: () => void
    filterOptions: FilterOption[]
    labelOptions?: FilterOption[]
    readOnly: boolean
    disabled: boolean
    /** True for the row that was just added: the field picker takes focus on mount. */
    autoFocus: boolean
}

const RuleRow = ({
                     rule,
                     setRule,
                     onDelete,
                     onDuplicate,
                     filterOptions,
                     labelOptions,
                     readOnly,
                     disabled,
                     autoFocus
                 }: RuleRowProps) => {
    const {t, language} = useLocalize()
    const theme = useTheme()
    const valueRef = useRef<HTMLDivElement>(null)
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
    const closeMenu = () => setMenuAnchor(null)

    const lookupOptions = useMemo(() => {
        const byField = new Map<string, FilterOption>()
        ;[...(labelOptions || []), ...filterOptions].forEach(option => byField.set(option.field, option))
        return Array.from(byField.values())
    }, [filterOptions, labelOptions])

    const pickerFields = useMemo(() => {
        const base = filterOptions.map(option => option.field)
        return rule.field && !base.includes(rule.field) ? [...base, rule.field] : base
    }, [filterOptions, rule.field])

    const selectedOption = lookupOptions.find(option => option.field === rule.field)
    const valueless = !!rule.operator && VALUELESS_OPERATORS.has(rule.operator)
    const isExclusive = rule.isExclusive === true
    const isDeactivated = rule.isDeactivated === true
    const locked = readOnly || disabled

    const focusValue = () => requestAnimationFrame(() => {
        const input = valueRef.current?.querySelector<HTMLInputElement>('input:not([type="hidden"])')
        input?.focus()
        input?.select()
    })

    // The "not" marker sits in the field's cell, left of the picker, so an excluded row keeps its columns.
    const notChip = isExclusive ? (
        <Box sx={{display: 'inline-flex', flexShrink: 0}}>
            <ElementChip
                text={t('Not')}
                startIcon={<BlockIcon sx={{fontSize: 15, color: theme.palette.error.main}}/>}
                borderColor={alpha(theme.palette.error.main, 0.34)}
                backgroundColor={alpha(theme.palette.error.main, 0.06)}
            />
        </Box>
    ) : null

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: ROW_COLUMNS,
                columnGap: 1,
                alignItems: 'center',
                minHeight: ROW_HEIGHT,
                opacity: isDeactivated ? 0.45 : 1,
                '& .MuiInputBase-input, & .MuiSelect-select, & .MuiChip-label': {
                    textDecoration: isDeactivated ? 'line-through' : 'none',
                },
            }}
        >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minWidth: 0}}>
                {notChip}

                {/* A field the options no longer know stays in the list under its raw key or stored
                    label, so the row never goes blank. */}
                <Box sx={{flex: 1, minWidth: 0}}>
                    <SingleSelect
                        placeholder={t('Field')}
                        value={rule.field || ''}
                        options={pickerFields.map(field => ({
                            value: field,
                            label: getFilterOptionLabel(lookupOptions, field, language) || field
                        }))}
                        onChange={value => {
                            const option = filterOptions.find(candidate => candidate.field === value)
                            setRule({
                                ...rule,
                                field: value,
                                operator: option?.allowedOperators?.[0],
                                doubleValues: [],
                                localDateValues: [],
                                longValues: [],
                                textValues: [],
                                timestampValues: [],
                            })
                            if (value) focusValue()
                        }}
                        disabled={locked || isDeactivated}
                        autoFocus={autoFocus}
                    />
                </Box>
            </Box>

            {/* A stored operator the field no longer allows stays visible as "(no longer available)", so
                the owner can see what to replace. The backend ignores such a rule when it counts. */}
            <Box sx={{minWidth: 0, gridColumn: valueless ? 'span 2' : 'auto'}}>
                <SingleSelect
                    placeholder={t('Operator')}
                    value={rule.operator || ''}
                    options={(selectedOption?.allowedOperators ?? []).map(operator => {
                        const dated = selectedOption!.dataType === DataType.LOCAL_DATE || selectedOption!.dataType === DataType.TIMESTAMP
                        return {
                            value: operator,
                            label: dated ? t(`Data type operator ${operator} date`) : t(`Data type operator ${operator}`)
                        }
                    })}
                    unknownLabel={rule.operator ? t(`Data type operator ${rule.operator}`) : undefined}
                    onChange={value => {
                        const next = value as DataTypeOperator
                        // Values left behind by the previous operator would be stale noise for a value-less one.
                        setRule({...rule, operator: next, ...(VALUELESS_OPERATORS.has(next) ? NO_VALUES : {})})
                    }}
                    disabled={locked || isDeactivated || !selectedOption}
                    disableClearable
                />
            </Box>

            {!valueless && (
                <Box
                    ref={valueRef}
                    sx={{minWidth: 0, pointerEvents: (locked || isDeactivated) ? 'none' : 'auto'}}
                >
                    {selectedOption ? (
                        <GenericValueInput
                            dataType={selectedOption.dataType}
                            filterRow={rule}
                            setFilterRow={setRule}
                            option={selectedOption}
                        />
                    ) : (
                        <TextField size={'small'} hiddenLabel disabled fullWidth/>
                    )}
                </Box>
            )}

            <IconButton
                size={'small'}
                aria-label={t('Rule options')}
                disabled={locked}
                onClick={event => setMenuAnchor(event.currentTarget)}
            >
                <MoreVertIcon/>
            </IconButton>

            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
                <MenuItem onClick={() => {
                    setRule({...rule, isExclusive: !isExclusive});
                    closeMenu()
                }}>
                    <ListItemIcon><BlockIcon fontSize={'small'}/></ListItemIcon>
                    <ListItemText>{isExclusive ? t('Include matches') : t('Exclude matches')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => {
                    setRule({...rule, isDeactivated: !isDeactivated});
                    closeMenu()
                }}>
                    <ListItemIcon>{isDeactivated ? <VisibilityIcon fontSize={'small'}/> :
                        <VisibilityOffIcon fontSize={'small'}/>}</ListItemIcon>
                    <ListItemText>{isDeactivated ? t('Enable rule') : t('Disable rule')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => {
                    onDuplicate();
                    closeMenu()
                }}>
                    <ListItemIcon><ContentCopyIcon fontSize={'small'}/></ListItemIcon>
                    <ListItemText>{t('Duplicate')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => {
                    onDelete();
                    closeMenu()
                }} sx={{color: 'error.main'}}>
                    <ListItemIcon><DeleteIcon fontSize={'small'} color={'error'}/></ListItemIcon>
                    <ListItemText>{t('Delete rule')}</ListItemText>
                </MenuItem>
            </Menu>
        </Box>
    )
}

// ------------------------------------------------------------------------------------------- box

interface RuleBoxProps {
    group: FilterGroup
    setGroup: (group: FilterGroup) => void
    /** The last rule went: the parent drops this box or sub-group from its list. */
    onEmpty: () => void
    filterOptions: FilterOption[]
    labelOptions?: FilterOption[]
    readOnly: boolean
    disabled: boolean
    /** 0 is a box; anything deeper is a sub-group with its own operator. */
    depth: number
    /** True for a sub-group that was just added: its first rule takes focus, as a new rule does. */
    focusFirstRule?: boolean
}

const RuleBox = ({
                     group,
                     setGroup,
                     onEmpty,
                     filterOptions,
                     labelOptions,
                     readOnly,
                     disabled,
                     depth,
                     focusFirstRule
                 }: RuleBoxProps) => {
    const {t} = useTranslation()
    const theme = useTheme()
    const [focusIndex, setFocusIndex] = useState<number | null>(focusFirstRule ? 0 : null)
    const [focusSubIndex, setFocusSubIndex] = useState<number | null>(null)
    const isSubGroup = depth > 0
    const operator = group.operator ?? DataFilterOperator.AND
    const rules = group.filterRules ?? []
    const subGroups = group.subGroups ?? []
    const locked = readOnly || disabled

    const update = (next: FilterGroup) => groupIsEmpty(next) ? onEmpty() : setGroup(next)
    const setRules = (nextRules: FilterRule[]) => update({...group, filterRules: nextRules})
    const setSubGroups = (nextSubGroups: FilterGroup[]) => update({...group, subGroups: nextSubGroups})

    const addRule = () => {
        setFocusIndex(rules.length)
        setGroup({...group, filterRules: [...rules, emptyRule()]})
    }

    const addSubGroup = () => {
        setFocusSubIndex(subGroups.length)
        setGroup({
            ...group,
            subGroups: [...subGroups, {operator: DataFilterOperator.OR, filterRules: [emptyRule()]}],
        })
    }

    const body = (
        <>
            <Stack gap={1}>
                {rules.map((rule, index) => (
                    <RuleRow
                        key={`rule-${index}`}
                        rule={rule}
                        autoFocus={focusIndex === index}
                        setRule={nextRule => {
                            const nextRules = [...rules]
                            nextRules[index] = nextRule
                            setRules(nextRules)
                        }}
                        onDelete={() => setRules(rules.filter((_, candidate) => candidate !== index))}
                        onDuplicate={() => {
                            const nextRules = [...rules]
                            nextRules.splice(index + 1, 0, {...rule})
                            setRules(nextRules)
                        }}
                        filterOptions={filterOptions}
                        labelOptions={labelOptions}
                        readOnly={readOnly}
                        disabled={disabled}
                    />
                ))}

                {subGroups.map((subGroup, index) => (
                    <RuleBox
                        key={`sub-${index}`}
                        group={subGroup}
                        depth={depth + 1}
                        focusFirstRule={focusSubIndex === index}
                        setGroup={nextSub => {
                            const nextSubGroups = [...subGroups]
                            nextSubGroups[index] = nextSub
                            setSubGroups(nextSubGroups)
                        }}
                        onEmpty={() => setSubGroups(subGroups.filter((_, candidate) => candidate !== index))}
                        filterOptions={filterOptions}
                        labelOptions={labelOptions}
                        readOnly={readOnly}
                        disabled={disabled}
                    />
                ))}
            </Stack>

            {!readOnly && (
                <Stack direction={'row'} gap={1} sx={{mt: 1}}>
                    <Button size={'small'} variant={'text'} startIcon={<AddIcon/>} disabled={disabled}
                            onClick={addRule}>
                        {t('Add rule')}
                    </Button>
                    <Button size={'small'} variant={'text'} startIcon={<AddIcon/>} disabled={disabled}
                            onClick={addSubGroup}>
                        {t('Add sub-group')}
                    </Button>
                </Stack>
            )}
        </>
    )

    if (isSubGroup) {
        // A sub-group is a section of its box: a strong accent on the left, a faint tint, and its
        // header is the one operator choice in the whole builder.
        return (
            <Box
                sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    borderLeft: `4px solid ${theme.palette.warning.main}`,
                    bgcolor: alpha(theme.palette.warning.main, 0.04),
                }}
            >
                <Stack direction={'row'} alignItems={'center'} gap={1} sx={{mb: 1}}>
                    <NewStatusPicker<DataFilterOperator>
                        value={operator}
                        disabled={locked}
                        ariaLabel={t('Match')}
                        onChange={next => setGroup({...group, operator: next})}
                        options={[
                            {value: DataFilterOperator.OR, label: t('Any of the following')},
                            {value: DataFilterOperator.AND, label: t('All of the following')},
                        ]}
                    />
                </Stack>
                {body}
            </Box>
        )
    }

    return (
        <Paper variant={'outlined'} sx={{p: 1.5}}>
            <Typography variant={'subtitle2'} color={'text.secondary'} sx={{mb: 1}}>
                {t('WHERE')}
            </Typography>
            {body}
        </Paper>
    )
}

// ------------------------------------------------------------------------------------- history

/**
 * Snapshot undo/redo over the filter, the way the automation editor keeps its canvas history.
 * `commit` is the only way edits leave this component; `remember` alone is for changes the
 * caller applies itself (template, generated filter) so they are undoable too.
 */
const useFilterHistory = (filter: Filter, setFilter: (filter: Filter) => void) => {
    const past = useRef<Filter[]>([])
    const future = useRef<Filter[]>([])
    const [, rerender] = useState(0)
    const touch = () => rerender(count => count + 1)

    const remember = () => {
        past.current = [...past.current.slice(-(HISTORY_LIMIT - 1)), filter]
        future.current = []
        touch()
    }

    const commit = (next: Filter) => {
        remember()
        setFilter(next)
    }

    const undo = () => {
        const previous = past.current.pop()
        if (!previous) return
        future.current.push(filter)
        setFilter(previous)
        touch()
    }

    const redo = () => {
        const next = future.current.pop()
        if (!next) return
        past.current.push(filter)
        setFilter(next)
        touch()
    }

    return {commit, remember, undo, redo, canUndo: past.current.length > 0, canRedo: future.current.length > 0}
}

// ------------------------------------------------------------------------------------------ root

const NewAudienceFilters = (props: NewAudienceFiltersProps) => {
    const {
        filter: rawFilter,
        setFilter,
        filterOptions,
        labelOptions,
        templates = [],
        templatesLoading,
        onSelectTemplate,
        onGenerated
    } = props
    const readOnly = props.readOnly ?? false
    // Whatever shape the caller holds, the builder works on the fixed model; the first edit saves it.
    const filter = useMemo(() => toCanonical(rawFilter), [rawFilter])
    const {t} = useTranslation()
    const {enqueueSnackbar} = useSnackbar()
    const history = useFilterHistory(filter, setFilter)

    const [query, setQuery] = useState<string>(props.initialQuery ?? '')
    const [aiOpen, setAiOpen] = useState(false)
    const [templateMenuAnchor, setTemplateMenuAnchor] = useState<HTMLElement | null>(null)
    const templateMenuOpen = Boolean(templateMenuAnchor)
    const instructionRef = useRef<HTMLTextAreaElement>(null)
    useEffect(() => {
        if (!aiOpen) return
        instructionRef.current?.focus()
        instructionRef.current?.select()
    }, [aiOpen])
    const [instruction, setInstruction] = useState('')
    const cancelled = useRef(false)

    const groups = filter.filterGroups ?? []
    const hasRules = filterHasRules(filter)

    const generation = useMutation(
        async (description: string) => AudienceAiService.getFilterSuggestions(description),
        {
            onMutate: () => {
                cancelled.current = false
            },
            onSuccess: result => {
                if (cancelled.current) return
                // The backend prunes rules on hidden/unknown fields, so a vague description can come
                // back with no rules at all. Say so instead of silently returning to the empty state.
                if (!result || !filterHasRules(result)) {
                    enqueueSnackbar(
                        t('The AI could not turn that description into rules, try describing the guests differently'),
                        {variant: 'warning'}
                    )
                    return
                }
                history.remember()
                onGenerated?.(toCanonical(result), query.trim())
            },
            onError: error => {
                if (cancelled.current) return
                enqueueSnackbar(
                    t('Could not create a filter: {{msg}}', {msg: getErrorMessage(error, t('Unexpected error'))}),
                    {variant: 'error'}
                )
            },
        }
    )
    const generating = generation.isLoading

    const cancelGeneration = () => {
        cancelled.current = true
        generation.reset()
    }

    const change = useMutation(
        async (text: string) => AudienceAiService.changeFilter(filter, text),
        {
            onSuccess: result => {
                if (!result || !filterHasRules(result)) {
                    enqueueSnackbar(t('The AI could not apply that change, the rules are unchanged'), {variant: 'warning'})
                    return
                }
                history.commit(toCanonical(result))
                setInstruction('')
                setAiOpen(false)
                enqueueSnackbar(t('Rules updated'), {variant: 'success'})
            },
            onError: error => {
                enqueueSnackbar(
                    t('Could not change the rules: {{msg}}', {msg: getErrorMessage(error, t('Unexpected error'))}),
                    {variant: 'error'}
                )
            },
        }
    )
    const changing = change.isLoading
    // Anything that rewrites the filter from outside locks the builder until it has landed.
    const busy = generating || changing

    const setGroups = (nextGroups: FilterGroup[]) =>
        history.commit({...filter, operator: DataFilterOperator.OR, filterGroups: nextGroups})

    const addBox = () => setGroups([...groups, newBox()])

    const addFirstRuleByHand = () =>
        history.commit({...filter, operator: DataFilterOperator.OR, filterGroups: [newBox()]})

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z') return
        event.preventDefault()
        if (event.shiftKey) history.redo()
        else history.undo()
    }

    return (
        <Stack gap={1.5} onKeyDown={onKeyDown}>
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={1}>
                <Box>
                    <Typography variant={'subtitle1'} sx={{mb: -2, fontWeight: 600}}>
                        {hasRules ? t('Rules — guests matching any box') : t('Rules')}
                    </Typography>
                </Box>

                {/* Always rendered so the header keeps one height; each button is disabled while its
                    action is impossible rather than hidden. */}
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                    <IconButton aria-label={t('Undo')} disabled={readOnly || busy || !history.canUndo}
                                onClick={history.undo}>
                        <UndoIcon/>
                    </IconButton>
                    <IconButton aria-label={t('Redo')} disabled={readOnly || busy || !history.canRedo}
                                onClick={history.redo}>
                        <RedoIcon/>
                    </IconButton>
                    <Tooltip title={t('Change with AI')}>
                        <span>
                            <IconButton
                                aria-label={t('Change with AI')}
                                color={aiOpen && hasRules ? 'primary' : 'default'}
                                disabled={readOnly || busy || !hasRules}
                                onClick={() => setAiOpen(open => !open)}
                            >
                                <AutoAwesomeIcon/>
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            {aiOpen && hasRules && !readOnly && (
                <Paper variant={'outlined'} sx={{p: 2, bgcolor: 'action.hover', borderColor: 'primary.light'}}>
                    <Typography variant={'subtitle2'} color={'text.secondary'} sx={{mb: 1}}>
                        {t('Describe the change')}
                    </Typography>
                    <TextField
                        multiline
                        rows={3}
                        fullWidth
                        inputRef={instructionRef}
                        value={instruction}
                        disabled={changing}
                        onChange={event => setInstruction(event.target.value)}
                        placeholder={t('e.g. only guests who accept marketing, and drop the country rule')}
                    />
                    <Stack direction={'row'} justifyContent={'flex-end'} sx={{mt: 1.5}}>
                        <LoadingButton
                            variant={'contained'}
                            startIcon={<AutoAwesomeIcon/>}
                            loading={changing}
                            disabled={!instruction.trim() || generating}
                            onClick={() => change.mutate(instruction.trim())}
                        >
                            {t('Apply')}
                        </LoadingButton>
                    </Stack>
                </Paper>
            )}

            {generating ? (
                <Paper variant={'outlined'} sx={{p: 1.5}}>
                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} sx={{mb: 1}}>
                        <Typography variant={'subtitle2'} color={'primary.main'}>
                            {t('Generating rules')}
                        </Typography>
                        <Button onClick={cancelGeneration}>{t('Cancel')}</Button>
                    </Stack>
                    <Typography variant={'body2'} color={'text.secondary'} sx={{mb: 1.5}}>
                        “{query.trim()}”
                    </Typography>
                    <Stack gap={1}>
                        {[0, 1, 2].map(row => (
                            <Box key={row} sx={{display: 'grid', gridTemplateColumns: ROW_COLUMNS, columnGap: 1}}>
                                <Skeleton variant={'rounded'} height={ROW_HEIGHT}/>
                                <Skeleton variant={'rounded'} height={ROW_HEIGHT}/>
                                <Skeleton variant={'rounded'} height={ROW_HEIGHT}/>
                                <span/>
                            </Box>
                        ))}
                    </Stack>
                </Paper>
            ) : !hasRules ? (
                <Paper variant={'outlined'} sx={{p: 2}}>
                    <TextField
                        id={'audience_ai_query_input'}
                        placeholder={t('Describe the guests, for example "All female guests from Germany who stayed at least twice"')}
                        size={'small'}
                        multiline
                        rows={5}
                        fullWidth
                        disabled={readOnly}
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                    />
                    <Stack
                        direction={'row'}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                        flexWrap={'wrap'}
                        gap={1}
                        sx={{mt: 1.5}}
                    >
                        <Stack direction={'row'} flexWrap={'wrap'} gap={0.5}>
                            <Button
                                id={'audience_template_button'}
                                variant={'text'}
                                color={'inherit'}
                                endIcon={<ExpandMoreIcon/>}
                                disabled={readOnly}
                                aria-haspopup={'menu'}
                                aria-controls={templateMenuOpen ? 'audience_template_menu' : undefined}
                                aria-expanded={templateMenuOpen ? 'true' : undefined}
                                onClick={event => setTemplateMenuAnchor(event.currentTarget)}
                                sx={{color: 'text.secondary'}}
                            >
                                {t('Use a template')}
                            </Button>
                            <Button
                                variant={'text'}
                                color={'inherit'}
                                startIcon={<AddIcon/>}
                                disabled={readOnly}
                                onClick={addFirstRuleByHand}
                                sx={{color: 'text.secondary'}}
                            >
                                {t('Add a rule by hand')}
                            </Button>
                        </Stack>
                        <LoadingButton
                            variant={'contained'}
                            startIcon={<AutoAwesomeIcon/>}
                            loading={generating}
                            disabled={readOnly || query.trim().length === 0}
                            onClick={() => generation.mutate(query.trim())}
                        >
                            {t('Generate')}
                        </LoadingButton>
                    </Stack>
                    <Menu
                        id={'audience_template_menu'}
                        anchorEl={templateMenuAnchor}
                        open={templateMenuOpen}
                        onClose={() => setTemplateMenuAnchor(null)}
                        MenuListProps={{'aria-labelledby': 'audience_template_button'}}
                        PaperProps={{sx: {minWidth: 300, maxWidth: 440}}}
                    >
                        {templatesLoading && (
                            <MenuItem disabled>{t('Loading...')}</MenuItem>
                        )}
                        {!templatesLoading && templates.length === 0 && (
                            <MenuItem disabled>{t('No templates available')}</MenuItem>
                        )}
                        {templates.map(template => (
                            <MenuItem
                                key={template.tags?.audienceTemplate || template.name}
                                sx={{whiteSpace: 'normal'}}
                                onClick={() => {
                                    setTemplateMenuAnchor(null)
                                    history.remember()
                                    onSelectTemplate?.(template)
                                }}
                            >
                                <ListItemText
                                    primary={t(template.name || '')}
                                    secondary={template.description ? t(template.description) : undefined}
                                />
                            </MenuItem>
                        ))}
                    </Menu>
                </Paper>
            ) : (
                <Stack gap={1.5}>
                    {groups.map((group, index) => (
                        <React.Fragment key={`box-${index}`}>
                            {index > 0 && (
                                <Typography
                                    variant={'body2'}
                                    align={'center'}
                                    color={'success.main'}
                                    sx={{fontWeight: 600, letterSpacing: '0.08em'}}
                                >
                                    {t('OR — alternatively')}
                                </Typography>
                            )}
                            <RuleBox
                                group={group}
                                depth={0}
                                setGroup={nextGroup => {
                                    const nextGroups = [...groups]
                                    nextGroups[index] = nextGroup
                                    setGroups(nextGroups)
                                }}
                                onEmpty={() => setGroups(groups.filter((_, candidate) => candidate !== index))}
                                filterOptions={filterOptions}
                                labelOptions={labelOptions}
                                readOnly={readOnly}
                                disabled={busy}
                            />
                        </React.Fragment>
                    ))}

                    {!readOnly && (
                        <Stack alignItems={'center'}>
                            <Button
                                variant={'text'}
                                color={'success'}
                                startIcon={<AddIcon/>}
                                disabled={busy}
                                onClick={addBox}
                            >
                                {t('OR box')}
                            </Button>
                        </Stack>
                    )}
                </Stack>
            )}
        </Stack>
    )
}

export default NewAudienceFilters
